import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { VERSION } from '../version';

/** Official Win a Prize / My Investments social accounts (the cleanest few).
 *  Connected via bundle.social; surfaced so visitors can actually follow us. */
const SOCIALS: { label: string; href: string; path: string }[] = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/my_investments619/',
    path: 'M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zm0 3.68A6.16 6.16 0 1 0 12 18.16 6.16 6.16 0 0 0 12 5.84zm0 10.16A4 4 0 1 1 12 8a4 4 0 0 1 0 8zm6.4-10.4a1.44 1.44 0 1 1-2.88 0 1.44 1.44 0 0 1 2.88 0z',
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@myinvestments130',
    path: 'M16.6 5.82a4.28 4.28 0 0 1-1.06-2.82h-3.2v12.9a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 0 1 0-5.18c.27 0 .53.04.77.12v-3.3a5.86 5.86 0 0 0-.77-.05 5.84 5.84 0 1 0 5.84 5.84V9.0a7.4 7.4 0 0 0 4.35 1.4V7.2a4.28 4.28 0 0 1-3.34-1.38z',
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/@myinvestments-619',
    path: 'M23.5 6.2a3.02 3.02 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.51A3.02 3.02 0 0 0 .5 6.2 31.5 31.5 0 0 0 0 12a31.5 31.5 0 0 0 .5 5.8 3.02 3.02 0 0 0 2.12 2.14c1.88.51 9.38.51 9.38.51s7.5 0 9.38-.51a3.02 3.02 0 0 0 2.12-2.14A31.5 31.5 0 0 0 24 12a31.5 31.5 0 0 0-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z',
  },
];

/** "Follow us" social bar — reusable on its own (e.g. inside the rich landing
 *  footer) or via the full SiteFooter below. */
export function SocialBar({ className = '' }: { className?: string }) {
  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      <p className="text-sm font-semibold text-text-primary">Follow us</p>
      <div className="flex items-center gap-3">
        {SOCIALS.map((s) => (
          <a
            key={s.label}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Follow us on ${s.label}`}
            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-violet-50 text-violet-700 transition-colors duration-200 hover:bg-gradient-to-br hover:from-violet-600 hover:to-pink-500 hover:text-white"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d={s.path} />
            </svg>
          </a>
        ))}
      </div>
    </div>
  );
}

/** Shared public footer: a "Follow us" social bar + copyright + nav links.
 *  Brand-matched (aurora violet). Used on public pages without their own footer. */
export function SiteFooter() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-border bg-white py-10">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <SocialBar className="pb-6" />

        <div className="flex flex-col items-center gap-4 border-t border-border pt-6 sm:flex-row sm:justify-between">
          <p className="text-sm text-text-muted">
            {t('landing.footer_copyright', { year: new Date().getFullYear() })}
            <span className="ml-2 text-xs text-text-muted/70">v{VERSION}</span>
          </p>
          <nav aria-label="Footer navigation" className="flex flex-wrap items-center justify-center gap-6">
            <Link to="/how-it-works" className="text-sm text-text-muted transition hover:text-violet-700">
              {t('landing.howitworks_title')}
            </Link>
            <Link to="/winners" className="text-sm text-text-muted transition hover:text-violet-700">
              {t('landing.footer_winners')}
            </Link>
            <Link to="/partners" className="text-sm text-text-muted transition hover:text-violet-700">
              {t('landing.footer_partners')}
            </Link>
            <Link to="/register" className="text-sm text-text-muted transition hover:text-violet-700">
              {t('landing.footer_register')}
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}

export default SiteFooter;
